import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, Icon, Image, Modal, Text, Title, useComponentTheme } from '@vmos2/design-system';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SubscriptionLinks } from '../Subscriptions/SubscriptionLinks';

type useSubscriptionsHook = () => { error: boolean, isLoading: Boolean, openSubscriptionLink: () => Promise<void> };

type Props = {
  isOpen: boolean,
  onRequestClose: () => void,
  onLinkClicked?: () => void,
  useSubscriptions: useSubscriptionsHook,
};

export const SubscriptionsModal = ({ isOpen, onRequestClose, useSubscriptions, onLinkClicked }: Props) => {
  const { error, isLoading, openSubscriptionLink } = useSubscriptions();

  const { image } = useComponentTheme(theme => theme.components.SubscriptionsModal);
  const theme = useTheme();
  const { t } = useTranslation('subscriptions.modal');
  const price = t('price');
  const description = t('description');
  const footnote = t('footnote');
  const title = t('title');

  const openLink = useCallback(() => {
    onLinkClicked?.();
    openSubscriptionLink();
  }, [onLinkClicked, openSubscriptionLink]);

  return (
    <Modal
      contentLabel={title}
      footerRightSide={
        <CTAContainer>
          <Button
            block
            contentCenter
            data-test="subscriptions-modal-button"
            onClick={openLink}
            size="medium"
            type="button"
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? <Icon name="Spinner" spin /> : t('button')}
          </Button>
          {!!error && (
            <Text bold level={4} mt={2} type="danger">
              {t(`errors.${error.vmosCode}`, {
                defaultValue: t(`errors.default`),
              })}
            </Text>
          )}
        </CTAContainer>
      }
      footerSections={1}
      footerIgnoreThemeHeight
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="full-height"
    >
      <Content data-test="subscriptions-modal-content">
        {image && <SubscriptionImage src={image} />}

        {(title || price) && (
          <Intro>
            {title && (
              <Title level={1} mb={0} mode="SubscriptionsModal">
                {title}
              </Title>
            )}

            {price && (
              <Text color={theme.color.grey} level={5} strong>
                {price}
              </Text>
            )}
          </Intro>
        )}

        {(description || footnote) && (
          <Description>
            {description && <Text level={4}>{description}</Text>}

            {footnote && (
              <Text color={theme.color.grey} level={5}>
                {footnote}
              </Text>
            )}
          </Description>
        )}

        <SubscriptionLinks mt={2} />
      </Content>
    </Modal>
  );
};

const Content = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.margins.md};
`;

const Description = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.margins.xs};
`;

const SubscriptionImage = styled(Image)`
  width: 100%;
`;

const Intro = styled.div`
  display: grid;
  gap: 4px;
`;

const CTAContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
